import enLocale from '@BUP/locales/en/translation.json'
import esCLLocale from '@BUP/locales/es-cl/translation.json'
import esCOLocale from '@BUP/locales/es-co/translation.json'
import esMXLocale from '@BUP/locales/es-mx/translation.json'
import esPELocale from '@BUP/locales/es-pe/translation.json'

export const NO_APLICA = 'No aplica'

export const CANONICAL_AO_ORDER = [
  'AO00', 'AO01', 'AO02', 'AO03', 'AO04',
  'AO05', 'AO06', 'AO07', 'AO08', 'AO09'
]

const AO_NORMALIZATION_MAP = {
  AO10: 'AO00'
}

const AO_TO_GRADE_KEY = {
  AO00: '3',
  AO01: '4',
  AO02: '5',
  AO03: '6',
  AO04: '7',
  AO05: '8',
  AO06: '9',
  AO07: '10',
  AO08: '11',
  AO09: '12'
}

const DEFAULT_DISPLAY_NAMES = {
  AO00: '3º',
  AO01: '4º',
  AO02: '5º',
  AO03: '6º',
  AO04: '7º',
  AO05: '8º',
  AO06: 'Iº',
  AO07: 'IIº',
  AO08: 'IIIº',
  AO09: 'IVº'
}

const LOCALE_COMPATIBILITY_MAP = {
  'es-cl': {
    AO00: '3°básico',
    AO01: '4°básico',
    AO02: '5°básico',
    AO03: '6°básico',
    AO04: '7°básico',
    AO05: '8°básico',
    AO06: 'I°medio',
    AO07: 'II°medio',
    AO08: 'III°medio',
    AO09: 'IV°medio'
  },
  'es-mx': {
    AO00: null,
    AO01: '4° de primaria',
    AO02: '5° de primaria',
    AO03: '6° de primaria',
    AO04: '1° de secundaria',
    AO05: '2° de secundaria',
    AO06: '3° de secundaria',
    AO07: '4° de preparatoria',
    AO08: '5° de preparatoria',
    AO09: '6° de preparatoria'
  },
  'es-co': {
    AO00: null,
    AO01: '4° de primaria',
    AO02: '5° de primaria',
    AO03: '6° de bachillerato',
    AO04: '7° de bachillerato',
    AO05: '8° de bachillerato',
    AO06: '9° de bachillerato',
    AO07: '10° de bachillerato',
    AO08: '11° de bachillerato',
    AO09: null
  },
  'es-pe': {
    AO00: null,
    AO01: '4° Primaria',
    AO02: '5° Primaria',
    AO03: '6° Primaria',
    AO04: '1° Secundaria',
    AO05: '2° Secundaria',
    AO06: '3° Secundaria',
    AO07: '4° Secundaria',
    AO08: '5° Secundaria',
    AO09: null
  },
  'en-us': {
    AO00: '3rd Grade',
    AO01: '4th Grade',
    AO02: '5th Grade',
    AO03: '6th Grade',
    AO04: '7th Grade',
    AO05: '8th Grade',
    AO06: '9th Grade',
    AO07: '10th Grade',
    AO08: '11th Grade',
    AO09: '12th Grade'
  }
}

const BASE_LOCALE_TAGS = {
  en: enLocale?.common?.resources?.course?.values?.tags?.nivel || {},
  'es-cl': esCLLocale?.common?.resources?.course?.values?.tags?.nivel || {},
  'es-mx': esMXLocale?.common?.resources?.course?.values?.tags?.nivel || {},
  'es-co': esCOLocale?.common?.resources?.course?.values?.tags?.nivel || {},
  'es-pe': esPELocale?.common?.resources?.course?.values?.tags?.nivel || {}
}

const normalizeLocale = (locale) => (locale || '').toLowerCase()

const getLocaleKey = (locale) => {
  const normalizedLocale = normalizeLocale(locale)
  if (LOCALE_COMPATIBILITY_MAP[normalizedLocale]) return normalizedLocale
  if (normalizedLocale.startsWith('es-cl')) return 'es-cl'
  if (normalizedLocale.startsWith('es-mx')) return 'es-mx'
  if (normalizedLocale.startsWith('es-co')) return 'es-co'
  if (normalizedLocale.startsWith('es-pe')) return 'es-pe'
  if (normalizedLocale.startsWith('en-us')) return 'en-us'
  return null
}

const getBaseLocaleKey = (locale) => {
  const normalizedLocale = normalizeLocale(locale)
  if (normalizedLocale.startsWith('es-cl')) return 'es-cl'
  if (normalizedLocale.startsWith('es-mx')) return 'es-mx'
  if (normalizedLocale.startsWith('es-co')) return 'es-co'
  if (normalizedLocale.startsWith('es-pe')) return 'es-pe'
  if (normalizedLocale.startsWith('en-us')) return 'en'
  if (normalizedLocale.startsWith('en')) return 'en'
  return null
}

export const normalizeAoCode = (code) => AO_NORMALIZATION_MAP[code] || code

const getCanonicalLabel = (aoCode) => `Nivel ${aoCode}`

const getCatalogLabel = (aoCode, locale) => {
  const gradeKey = AO_TO_GRADE_KEY[aoCode]
  if (!gradeKey) return null

  const baseLocaleKey = getBaseLocaleKey(locale)
  if (baseLocaleKey && BASE_LOCALE_TAGS[baseLocaleKey]?.[gradeKey]) {
    return BASE_LOCALE_TAGS[baseLocaleKey][gradeKey]
  }

  return BASE_LOCALE_TAGS.en?.[gradeKey] || null
}

export const getLevelLabel = (aoCode, locale, fallbackLabel) => {
  const canonicalCode = normalizeAoCode(aoCode)
  if (!CANONICAL_AO_ORDER.includes(canonicalCode)) {
    return fallbackLabel || getCanonicalLabel(canonicalCode)
  }

  const localeKey = getLocaleKey(locale)
  const localeMap = localeKey ? LOCALE_COMPATIBILITY_MAP[localeKey] : null

  if (localeMap && Object.prototype.hasOwnProperty.call(localeMap, canonicalCode)) {
    const localizedLabel = localeMap[canonicalCode]
    if (localizedLabel === null) return NO_APLICA
    if (localizedLabel) return localizedLabel
  }

  const catalogLabel = getCatalogLabel(canonicalCode, locale)
  if (catalogLabel) return catalogLabel

  return fallbackLabel || getCanonicalLabel(canonicalCode)
}

export const getPreferredResponseCode = ({ canonicalCode, sourceCodes, sourceMap }) => {
  if (sourceCodes.includes('AO10') && sourceMap?.AO10 !== undefined) {
    return 'AO10'
  }
  return canonicalCode
}

const getDisplayName = (canonicalCode, label) => {
  if (label === NO_APLICA) return NO_APLICA
  return DEFAULT_DISPLAY_NAMES[canonicalCode] || canonicalCode
}

export const sanitizeSelectedLevel = (selectedLevel, levels, defaultValue = null) => {
  if (!selectedLevel) return selectedLevel
  const levelCodes = levels.map(level => level.code)
  return levelCodes.includes(selectedLevel) ? selectedLevel : defaultValue
}

export const buildLevelMapping = (surveySettings, locale) => {
  const levelSettings = surveySettings?.enivel || {}
  const uniqueLevels = new Map()

  Object.keys(levelSettings).forEach((rawCode) => {
    const canonicalCode = normalizeAoCode(rawCode)
    if (!CANONICAL_AO_ORDER.includes(canonicalCode)) return

    const existing = uniqueLevels.get(canonicalCode)
    const sourceCodes = existing?.sourceCodes || []
    if (!sourceCodes.includes(rawCode)) {
      sourceCodes.push(rawCode)
    }

    uniqueLevels.set(canonicalCode, {
      code: canonicalCode,
      sourceCodes,
      settings: existing?.settings || levelSettings[rawCode] || {}
    })
  })

  return CANONICAL_AO_ORDER
    .filter(code => uniqueLevels.has(code))
    .map((code) => {
      const level = uniqueLevels.get(code)
      const label = getLevelLabel(code, locale)
      return {
        ...level,
        level: code,
        label,
        displayName: getDisplayName(code, label)
      }
    })
    .filter(level => level.label !== NO_APLICA)
}
